import React, { memo, useState } from 'react';
import {
  DEMO_BRAND,
  DEMO_CLAIMS,
  DEMO_DRIFT,
  DEMO_RANKS,
  DEMO_SOV,
  DemoTab,
} from './demoData';

const TABS: { id: DemoTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'claims', label: 'Claims' },
  { id: 'drift', label: 'Drift' },
];

function claimPillClass(status: string): string {
  const key = status.toLowerCase();
  return `brand-landing-demo__pill brand-landing-demo__pill--${key}`;
}

function BrandLandingDemo() {
  const [tab, setTab] = useState<DemoTab>('overview');

  return (
    <div className="brand-landing-demo" data-testid="brand-landing-demo">
      <div className="brand-landing-demo__tabs" role="tablist" aria-label="Brand product preview">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`brand-landing-demo__tab${tab === t.id ? ' is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="brand-landing-demo__body" role="tabpanel">
        <div className="brand-landing-demo__title-row">
          <h3>{DEMO_BRAND.name}</h3>
          <span className="brand-landing-demo__badge">Sample audit · Live preview</span>
        </div>

        {tab === 'overview' && (
          <>
            <div className="brand-landing-demo__sov">
              {DEMO_SOV.map((row) => (
                <div key={row.name} className="brand-landing-demo__sov-row">
                  <span>{row.name}</span>
                  <div className="brand-landing-demo__bar" aria-hidden="true">
                    <span style={{ width: `${row.pct}%`, background: row.color }} />
                  </div>
                  <strong>{row.pct}%</strong>
                </div>
              ))}
            </div>
            <div className="brand-landing-demo__ranks">
              {DEMO_RANKS.map((r) => (
                <div key={r.engine} className="brand-landing-demo__rank">
                  <span>{r.engine}</span>
                  <strong>#{r.rank}</strong>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === 'claims' && (
          <table className="brand-landing-demo__table">
            <thead>
              <tr>
                <th>Claim</th>
                <th>Status</th>
                <th>Engine</th>
              </tr>
            </thead>
            <tbody>
              {DEMO_CLAIMS.map((c) => (
                <tr key={c.text}>
                  <td>{c.text}</td>
                  <td>
                    <span className={claimPillClass(c.status)}>{c.status}</span>
                  </td>
                  <td>{c.engine}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === 'drift' && (
          <>
            <div className="brand-landing-demo__drift-stats">
              <div className="brand-landing-demo__stat">
                <span>Fixed</span>
                <strong>{DEMO_DRIFT.fixed}</strong>
              </div>
              <div className="brand-landing-demo__stat">
                <span>New</span>
                <strong>{DEMO_DRIFT.newIssues}</strong>
              </div>
              <div className="brand-landing-demo__stat">
                <span>Persisted</span>
                <strong>{DEMO_DRIFT.persisted}</strong>
              </div>
              <div className="brand-landing-demo__stat">
                <span>SOV change</span>
                <strong>{DEMO_DRIFT.sovDelta}</strong>
              </div>
            </div>
            <p className="brand-landing-demo__note">{DEMO_DRIFT.rankNote}</p>
            <table className="brand-landing-demo__table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Change</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {DEMO_DRIFT.rows.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td>{row.change}</td>
                    <td>{row.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

export default memo(BrandLandingDemo);
